import { useEffect, useRef, useState, useCallback, type FC } from "react";
import { findAddressByStreetAndHouseNumber } from "../../services/addresses";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import MapboxManager from "../../utils/mapboxManager";

interface LocationMapProps {
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  currentLocation: { latitude: number; longitude: number } | null;
  onAddressDetailsFetched: (addressDetails: {
    streetNumber: string;
    streetName: string;
    zipcode: string;
    city: string;
    country: string;
    fullAddress: string;
    source?: "search" | "gps";
    existingAddressId?: number | null;
  }) => void;
  /** When "search", skip reverse geocoding since AddressForm already has structured data */
  addressSource?: "search" | "gps";
}

const BERLIN_COORDINATES = { latitude: 52.52, longitude: 13.405 };

const LocationMap: FC<LocationMapProps> = ({
  isLoading,
  setIsLoading,
  setError,
  currentLocation,
  onAddressDetailsFetched,
  addressSource,
}) => {
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const marker = useRef<mapboxgl.Marker | null>(null);
  const [existingAddressFound, setExistingAddressFound] = useState(false);
  const mapboxManager = MapboxManager.getInstance();
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const fetchAddressDetails = useCallback(async (longitude: number, latitude: number) => {
    try {
      console.log("Fetching address details via reverse geocode...");
      
      const data = await mapboxManager.reverseGeocode(longitude, latitude);
      console.log("Geocoding response:", data);

      if (data.features && data.features.length > 0) {
        const feature = data.features[0];
        const addressContext = feature.properties?.context?.address || {};
        const streetName = addressContext.street_name || "";
        const streetNumber = addressContext.address_number || "";
        const postcode = feature.properties?.context?.postcode?.name || "";
        const city = feature.properties?.context?.place?.name || "";
        const country = feature.properties?.context?.country?.name || "";

        // Only update address details if user has selected a location
        if (currentLocation) {
          // Check if address exists in database first
          let foundExistingId: number | null = null;
          setExistingAddressFound(false);
          if (streetName && streetNumber) {
            try {
              const existingAddress = await findAddressByStreetAndHouseNumber(
                streetName,
                streetNumber,
                {
                  zipCode: postcode,
                  city,
                  country,
                }
              );

              if (existingAddress) {
                console.log("Found existing address in database:", existingAddress.id);
                setExistingAddressFound(true);
                foundExistingId = existingAddress.id;
                
                // Use database address data to avoid additional API calls if available
                if (existingAddress.zip_code && existingAddress.city) {
                  onAddressDetailsFetched({
                    streetNumber: existingAddress.house_number || streetNumber,
                    streetName: existingAddress.street || streetName,
                    zipcode: existingAddress.zip_code || postcode,
                    city: existingAddress.city || city,
                    country: existingAddress.country || country,
                    fullAddress: `${existingAddress.street} ${existingAddress.house_number}, ${existingAddress.zip_code} ${existingAddress.city}`,
                    source: "gps",
                    existingAddressId: foundExistingId,
                  });
                  return; // Skip API result processing
                }
              }
            } catch (error) {
              console.error("Error checking address existence:", error);
            }
          }

          onAddressDetailsFetched({
            streetNumber: streetNumber,
            streetName: streetName,
            zipcode: postcode,
            city: city,
            country: country,
            fullAddress: feature.properties?.full_address || "",
            source: "gps",
            existingAddressId: foundExistingId,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching address details:", error);
      setError("Fehler beim Abrufen der Adressdetails");
    }
  }, [mapboxManager, currentLocation, onAddressDetailsFetched, setError]);

  const debouncedFetchAddressDetails = useCallback((longitude: number, latitude: number) => {
    // Clear existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    // Set new timeout
    debounceTimeoutRef.current = setTimeout(() => {
      fetchAddressDetails(longitude, latitude);
    }, 500); // 500ms debounce
  }, [fetchAddressDetails]);

  // Initialize map
  useEffect(() => {
    console.log("Initializing map...");
    const containerId = "location-map";

    if (!mapContainer.current) {
      return;
    }

    // Ensure the container has an ID
    if (!mapContainer.current.id) {
      mapContainer.current.id = containerId;
    }

    const initMap = async () => {
      try {
        // Get map instance from manager
        map.current = await mapboxManager.getMap(containerId, {
          style: "mapbox://styles/mapbox/streets-v11",
          center: [BERLIN_COORDINATES.longitude, BERLIN_COORDINATES.latitude],
          zoom: 10,
          attributionControl: true,
          preserveDrawingBuffer: true,
        });

        // Add navigation control
        map.current.addControl(new mapboxgl.NavigationControl(), "top-right");

        // Add geolocate control with device-specific settings
        const ua = navigator.userAgent;
        const isIOS = /iPhone|iPod/.test(ua);
        const isIPadOS =
          ua.includes("iPad") ||
          (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
        const isAndroid = ua.includes("Android");
        const isSafari = ua.includes("Safari") && !ua.includes("Chrome");

        // Configure geolocate control options based on device
        const geolocateOptions = {
          positionOptions: {
            enableHighAccuracy: !(isIOS || isIPadOS || isSafari),
            timeout: isIOS || isIPadOS ? 20000 : isAndroid ? 15000 : 10000,
            maximumAge: 0,
          },
          trackUserLocation: true,
          showUserHeading: true,
        };

        console.log("Adding GeolocateControl with device-specific settings:", {
          isIOS,
          isIPadOS,
          isAndroid,
          isSafari,
          options: geolocateOptions,
        });

        map.current.addControl(
          new mapboxgl.GeolocateControl(geolocateOptions),
          "top-right"
        );

        // Initialize marker
        marker.current = new mapboxgl.Marker()
          .setLngLat([
            BERLIN_COORDINATES.longitude,
            BERLIN_COORDINATES.latitude,
          ])
          .addTo(map.current);

        setIsLoading(false);
      } catch (err) {
        console.error("Error initializing map:", err);
        setError("Fehler bei der Initialisierung der Karte");
        setIsLoading(false);
      }
    };

    initMap();

    // Cleanup function
    return () => {
      if (map.current) {
        // Let the manager handle cleanup
        mapboxManager.removeMap(containerId);
        map.current = null;
      }
    };
  }, []);

  // Update map when location changes
  useEffect(() => {
    if (currentLocation && map.current && marker.current) {
      console.log("Updating map location...");
      map.current.flyTo({
        center: [currentLocation.longitude, currentLocation.latitude],
        zoom: 14,
      });

      marker.current.setLngLat([
        currentLocation.longitude,
        currentLocation.latitude,
      ]);

      // Only reverse geocode if address came from GPS, NOT from search selection.
      // When source is "search", AddressForm already has structured address data
      // from the Mapbox Search API — reverse geocoding would overwrite it with
      // potentially different values from the Reverse Geocode API.
      if (addressSource !== "search") {
        debouncedFetchAddressDetails(currentLocation.longitude, currentLocation.latitude);
      }
    }
  }, [currentLocation, debouncedFetchAddressDetails, addressSource]);

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="relative h-full rounded-lg bg-white p-4 shadow-sm dark:bg-gray-800">
      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-gray-100 bg-opacity-75 dark:bg-gray-700 dark:bg-opacity-75">
          <div className="text-lg font-semibold text-gray-900 dark:text-white">
            Karte wird geladen...
          </div>
        </div>
      )}
      <div className="relative">
        <div ref={mapContainer} className="h-[600px] w-full rounded-lg" />
        {existingAddressFound && (
          <div className="absolute bottom-4 right-4 rounded-lg bg-green-100 p-2 text-sm text-green-800 dark:bg-green-900 dark:text-green-200">
            Bestehende Adresse gefunden!
          </div>
        )}
      </div>
    </div>
  );
};

export default LocationMap;
